import { useCallback, useEffect, useRef, useState } from 'react';
import { isNil } from 'swipeclock-frontend';
import { STABLE_ONLINE_DEBOUNCE_MS } from '../constants/offlineStatusToolbarLabels';

const NETWORK_SOURCE = 'network';
const DEV_OVERRIDE_SOURCE = 'dev-override';

export function useOfflineConnectivitySnackbars({
  devIsOfflineOverride,
  effectiveIsOnline,
  isOfflineShellMode,
  onOnline,
}) {
  const isDevOverrideEnabled = !isNil(devIsOfflineOverride);
  const isStatusUiEnabled = isOfflineShellMode || isDevOverrideEnabled;
  const isOffline = !effectiveIsOnline;
  const connectivitySource = isDevOverrideEnabled
    ? DEV_OVERRIDE_SOURCE
    : NETWORK_SOURCE;

  const [shownThisPeriod, setShownThisPeriod] = useState(false);
  const [onlineSnackbarRequested, setOnlineSnackbarRequested] = useState(false);
  const closePersistentOfflineSnackbarRef = useRef(null);
  const onlineDebounceTimerRef = useRef(null);
  const previousConnectivityRef = useRef(null);
  const effectiveIsOnlineRef = useRef(effectiveIsOnline);
  const isStatusUiEnabledRef = useRef(isStatusUiEnabled);

  effectiveIsOnlineRef.current = effectiveIsOnline;
  isStatusUiEnabledRef.current = isStatusUiEnabled;

  const clearOnlineDebounceTimer = useCallback(() => {
    if (onlineDebounceTimerRef.current) {
      clearTimeout(onlineDebounceTimerRef.current);
      onlineDebounceTimerRef.current = null;
    }
  }, []);

  const registerClosePersistentOfflineSnackbar = useCallback((fn) => {
    closePersistentOfflineSnackbarRef.current = fn;
  }, []);

  const acknowledgeOfflineSnackbar = useCallback(() => {
    setShownThisPeriod(true);
  }, []);

  const acknowledgeOnlineSnackbar = useCallback(() => {
    setOnlineSnackbarRequested(false);
  }, []);

  useEffect(() => {
    const previousConnectivity = previousConnectivityRef.current;
    const currentConnectivity = {
      isOnline: effectiveIsOnline,
      isStatusUiEnabled,
      source: connectivitySource,
    };

    previousConnectivityRef.current = currentConnectivity;

    if (!isStatusUiEnabled) {
      clearOnlineDebounceTimer();
      setOnlineSnackbarRequested(false);
      return;
    }

    if (
      !previousConnectivity ||
      previousConnectivity.source !== connectivitySource ||
      !previousConnectivity.isStatusUiEnabled
    ) {
      clearOnlineDebounceTimer();
      setOnlineSnackbarRequested(false);
      if (effectiveIsOnline) {
        onOnline();
      }
      return;
    }

    if (previousConnectivity.isOnline === effectiveIsOnline) {
      return;
    }

    if (!effectiveIsOnline) {
      const hadPendingOnlineTimer = onlineDebounceTimerRef.current !== null;
      clearOnlineDebounceTimer();
      setOnlineSnackbarRequested(false);

      if (!hadPendingOnlineTimer) {
        setShownThisPeriod(false);
      }
      return;
    }

    onOnline();
    clearOnlineDebounceTimer();
    onlineDebounceTimerRef.current = setTimeout(() => {
      onlineDebounceTimerRef.current = null;
      if (!effectiveIsOnlineRef.current || !isStatusUiEnabledRef.current) {
        return;
      }
      closePersistentOfflineSnackbarRef.current?.();
      setOnlineSnackbarRequested(true);
      setShownThisPeriod(false);
    }, STABLE_ONLINE_DEBOUNCE_MS);
  }, [
    clearOnlineDebounceTimer,
    connectivitySource,
    effectiveIsOnline,
    isStatusUiEnabled,
    onOnline,
  ]);

  useEffect(
    () => () => {
      clearOnlineDebounceTimer();
      previousConnectivityRef.current = null;
    },
    [clearOnlineDebounceTimer]
  );

  return {
    acknowledgeOfflineSnackbar,
    acknowledgeOnlineSnackbar,
    isOffline,
    isStatusUiEnabled,
    offlineSnackbarRequested:
      isStatusUiEnabled && isOffline && !shownThisPeriod,
    onlineSnackbarRequested,
    registerClosePersistentOfflineSnackbar,
  };
}
